import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertCircle, RotateCw } from 'lucide-react';
import { cn } from 'lib/utils';
import { Skeleton } from 'components/ui/skeleton';
import { useAdminAuditEvents } from 'hooks/useAdminAuditEvents';
import EventRow from './EventRow';
import { GRID_COLS, type OpenEvent } from './tableColumns';

const CHILDREN_PAGE = 5;

interface ImportChildRowsProps {
  importId: string;
  onOpen: OpenEvent;
}

/** Child rows of one `technician_import` parent, fetched only once its row is expanded (CA19).
 * Mounting the component IS the "on demand": no fetch happens before this renders it. */
export const ImportChildRows = ({ importId, onOpen }: ImportChildRowsProps) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { events, isLoading, isError, refetch, hasNextPage } = useAdminAuditEvents({ importId });

  // Rows only, no wrapping rowgroup: they sit in the table's own rowgroup (ARIA table).
  if (isLoading) {
    return (
      <>
        {[0, 1].map((row) => (
          <div key={row} role="row" className={cn('grid items-center gap-3 border-b pl-8 pr-4', GRID_COLS)} style={{ height: 44 }}>
            <Skeleton role="cell" className="h-3.5 w-3/5" />
          </div>
        ))}
      </>
    );
  }

  if (isError) {
    return (
      <div role="row" className="border-b py-2 pl-8 pr-4">
        <div role="cell" className="flex items-center gap-2 text-[12.5px] text-rose-600 dark:text-rose-400">
          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
          {t('An error occurred')}
          <button type="button" onClick={() => refetch()} className="inline-flex items-center gap-1 font-medium underline hover:no-underline">
            <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
            {t('Retry')}
          </button>
        </div>
      </div>
    );
  }

  const visible = showAll ? events : events.slice(0, CHILDREN_PAGE);
  const remaining = events.length - visible.length;

  return (
    <>
      {visible.map((child) => (
        <EventRow key={child.id} event={child} onOpen={onOpen} dense />
      ))}
      {(remaining > 0 || hasNextPage) && (
        <div role="row" className="border-b py-2 pl-8">
          <div role="cell" className="flex items-center gap-4 text-[12.5px] font-medium text-muted-foreground">
            {remaining > 0 && (
              <button type="button" onClick={() => setShowAll(true)} className="underline hover:text-foreground hover:no-underline">
                {t('Show the other row', { count: remaining })}
              </button>
            )}
            {/* Past one page (200) the expansion stops: the filtered view pages the rest (T9). */}
            {hasNextPage && (
              <Link to={`/admin/audit?importId=${encodeURIComponent(importId)}`} className="underline hover:text-foreground hover:no-underline">
                {t('View the whole import')}
              </Link>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ImportChildRows;
